// libraries
import React, { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import Form from '/react4xp/components/Form/Form'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Radio from '/react4xp/components/Form/Radio'
import Input from '/react4xp/components/Form/Input'
import Select from '/react4xp/components/Form/Select'
import TextArea from '/react4xp/components/Form/TextArea'
import Submit from '/react4xp/components/Form/Submit'
import Image from '/react4xp/components/Image'

// utils
import { isNonWhitespaceString, slugify } from '/lib/reactutils/utils'

// svg
import { Close, Plus } from '/react4xp/components/Svg/Ux/Ux'
import { recaptchaCallbackFunction } from '/lib/reactutils/recaptcha'
import ReCaptcha from '../Recaptcha'
import Upload from '../Form/Upload'

export interface RMAFormProps {
	topInfos: {
		title: string
		intro: string
	}
	form: {
		endpoint: string
		onSuccess: () => void
		onError: () => void
	}
	steps: {
		step1: {
			title: string
			options: Array<{
				value: string
				helperText?: string
			}>
			errorMessage: string
		}
		step2: {
			title: string
			inputs: Array<{
				id: string
				name: string
				label: string
				type: 'text' | 'email' | 'tel'
				helperText?: string
				required?: boolean
				errorMessages?: {
					required: string
					invalidEmail?: string
				}
			}>
		}
		step3: {
			title: string,
			equipment: {
				title: string
				inputs: Array<{
					name: string
					label: string
				}>
				deleteButtonText: string
				addButtonText: string
			}
			additionalInformation: {
				id: string
				name: string
				label: string
				placeholder?: string
				helperText?: string
				required?: boolean
				errorMessages?: {
					required: string
				}
			}
			uploadFile?: {
				id: string
				name: string
				label: string
				helperText?: string
				required?: boolean
				errorMessages?: {
					format: string
					maxFileSize: string
				}
				multiple?: boolean
				maxFileSize?: number
				accept?: string
				uploadButtonText?: string
			}
		}
		step4: {
			title: string
			inputs: Array<{
				type: 'text' | 'email' | 'tel'
				id: string
				name: string
				label: string
				required?: boolean
				helperText?: string
				errorMessages?: {
					required: string
					invalidEmail?: string
				}
			}>
			country: {
				id: string
				name: string
				label: string
				defaultValue: string
				options: Array<{
					label: string
					value: string
				}>
				required?: boolean
				errorMessages?: {
					required: string
				}
			}
			billingAddress: {
				checkbox: {
					id: string
					name: string
					label: string
					text: string
				}
				title: string
				inputs: Array<{
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				}>
				country: {
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					errorMessages?: {
						required: string
					}
				}
			}
			preferredSolution: {
				id: string
				name: string
				label: string
				defaultValue: string
				options: Array<{
					label: string
					value: string
				}>
				helperText?: string
			}
		}
	}
	privacyCheckbox: {
		name: string
		required: boolean
		errorMessages: {
			required: string
		}
		text: string
	}
	submit: {
		label: string
		successMessage: string
	},
	siteKey?: string
	validationRecaptchaUrl?: string
}

export default function RMAForm({
	topInfos,
	form,
	steps,
	privacyCheckbox,
	submit,
	siteKey,
	validationRecaptchaUrl
}: RMAFormProps) {
	const [verified, setVerified] = useState(false)
	const [equipmentList, setEquipmentList] = useState([{ id: 1 }])
	const [differentDeliveryAddress, setDifferentDeliveryAddress] = useState(true)

	// add new equipment
	const addEquipment = () => {
		setEquipmentList([...equipmentList, { id: equipmentList.length + 1 }])
	}

	// delete equipment
	const deleteEquipment = (index) => {
		if (equipmentList.length > 1) {
			setEquipmentList(equipmentList.filter((_, i) => i !== index))
		}
	}

	// listen for form reset event
	useEffect(() => {
		const handleFormReset = () => {
			//setVerified(false)
			
			setEquipmentList([{ id: 1 }])
			setDifferentDeliveryAddress(true)
		}

		document.addEventListener('resetForm', handleFormReset)
		
		return () => {
			document.removeEventListener('resetForm', handleFormReset)
		}
	}, [])

	return (
		<div className='page--forms'>

			<Section textColor='text-dark' className='top-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<div className='grid-row'>
						<div className='c-12 c-md-10 c-lg-8'>

							{isNonWhitespaceString(topInfos?.title) && (
								<h1 className='title-lg top-section__title'>
									{topInfos?.title}
								</h1>
							)}

							{isNonWhitespaceString(topInfos?.intro) && (
								<p
									dangerouslySetInnerHTML={{ __html: topInfos?.intro }}
									suppressHydrationWarning
									className='top-section__intro'
								/>
							)}

						</div>
					</div>
				</div>
			</Section>

			<Section textColor='text-dark' className='form-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<Form
						endpoint={form?.endpoint}
						clearOnSuccess
						onSuccess={form?.onSuccess}
						onError={form?.onError}
					>

						<Input type="text" hidden value="rma_form" name="form_type" id="form_type" />

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>1.</span> {steps?.step1?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>

								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{steps?.step1.options.map((item, i) => (
										<Radio
											key={i}
											value={item.value}
											name='request-type'
											helperText={item.helperText}
											required
											errorMessages={{
												required: steps?.step1?.errorMessage
											}}
										>
											{item.value}
										</Radio>
									))}

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>2.</span> {steps?.step2?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>

								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{steps?.step2?.inputs.map((item, i) => (
										<Input
											key={i}
											type={item.type}
											id={item.id}
											name={item.name}
											label={item.label}
											helperText={item.helperText}
											required={item.required}
											errorMessages={item.errorMessages}
										/>
									))}

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>3.</span> {steps?.step3?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>

								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper content-wrapper--equipment'>

									{steps?.step3?.equipment && (
										<>
											{equipmentList.map((item, i) => (
												<div key={item.id} className='equipment-wrapper'>

													<div className='equipment-top-row'>

														<p className='labels-xxl-medium'>
															{steps?.step3?.equipment?.title} {i + 1}
														</p>

														{equipmentList.length > 1 && i > 0 && (
															<button
																className='delete-equipment labels-sm-medium'
																type='button'
																onClick={() => deleteEquipment(i)}
															>
																<Close />
																{steps?.step3?.equipment?.deleteButtonText}
															</button>
														)}

													</div>

													<div className='equipment-middle-row'>
														<div className='grid-row'>

															{steps?.step3?.equipment?.inputs.map((item2, i) => (
																<div className='c-sm-6' key={i}>
																	<Input
																		type='text'
																		id={slugify(item2.name) + `-${item.id}`}
																		name={`${item2.name} ${item.id}`}
																		label={item2.label}
																	/>
																</div>
															))}

														</div>
													</div>

												</div>
											))}

											<div className='equipment-bottom-row'>
												<button
													className='add-equipment'
													type='button'
													onClick={addEquipment}
												>
													<Plus />
													{steps?.step3?.equipment?.addButtonText}
												</button>
											</div>

										</>
									)}

									{steps?.step3?.additionalInformation && (
										<TextArea
											id={steps?.step3?.additionalInformation?.id}
											name={steps?.step3?.additionalInformation?.name}
											label={steps?.step3?.additionalInformation?.label}
											placeholder={steps?.step3?.additionalInformation?.placeholder}
											helperText={steps?.step3?.additionalInformation?.helperText}
											required={steps?.step3?.additionalInformation?.required}
											errorMessages={steps?.step3?.additionalInformation?.errorMessages}
										/>
									)}

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>4.</span> {steps?.step4?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>

								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{steps?.step4?.inputs.map((item, i) => (
										<Input
											key={i}
											type={item.type}
											id={item.id}
											name={item.name}
											label={item.label}
											required={item.required}
											errorMessages={item.errorMessages}
										/>
									))}

									{steps?.step4?.country && (
										<Select
											id={steps?.step4?.country?.id}
											name={steps?.step4?.country?.name}
											label={steps?.step4?.country?.label}
											defaultValue={steps?.step4?.country?.defaultValue}
											options={steps?.step4?.country?.options}
											required={steps?.step4?.country?.required}
											errorMessages={steps?.step4?.country?.errorMessages}
										/>
									)}

									{steps?.step4?.billingAddress && (
										<span className='double-space'>
											<Checkbox
												id={steps?.step4?.billingAddress?.checkbox?.id}
												name={steps?.step4?.billingAddress?.checkbox?.name}
												label={steps?.step4?.billingAddress?.checkbox?.label}
												onChange={() => setDifferentDeliveryAddress(!differentDeliveryAddress)}
												checked={differentDeliveryAddress}
											>
												{steps?.step4?.billingAddress?.checkbox?.text}
											</Checkbox>
										</span>
									)}

									{steps?.step4?.preferredSolution && (
										<Select
											id={steps?.step4?.preferredSolution?.id}
											name={steps?.step4?.preferredSolution?.name}
											label={steps?.step4?.preferredSolution?.label}
											defaultValue={steps?.step4?.preferredSolution?.defaultValue}
											options={steps?.step4?.preferredSolution?.options}
											helperText={steps?.step4?.preferredSolution?.helperText}
										/>
									)}

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						{!differentDeliveryAddress && (
							<>
								{steps?.step4?.billingAddress && (
									<>

										<div className='form-section__title'>
											<p className='labels-xxl-medium'>
												<span className='step-number'>5.</span> {steps?.step4?.billingAddress?.title}
											</p>
										</div>

										<div className='form-section__content form-section__content--space-left'>
											<div className='grid-row'>

												<div className='c-md-1 c-lg-2'></div>
												<div className='c-md-1 border'></div>

												<div className='c-md-9 c-lg-8 content-wrapper'>

													{steps?.step4?.billingAddress?.inputs.map((item, i) => (
														<Input
															key={i}
															type={item.type}
															id={item.id}
															name={item.name}
															label={item.label}
															required={item.required}
															errorMessages={item.errorMessages}
														/>
													))}

													{steps?.step4?.billingAddress?.country && (
														<Select
															id={steps?.step4?.billingAddress?.country?.id}
															name={steps?.step4?.billingAddress?.country?.name}
															label={steps?.step4?.billingAddress?.country?.label}
															defaultValue={steps?.step4?.billingAddress?.country?.defaultValue}
															options={steps?.step4?.billingAddress?.country?.options}
															required={steps?.step4?.billingAddress?.country?.required}
															errorMessages={steps?.step4?.billingAddress?.country?.errorMessages}
														/>
													)}

												</div>

												<div className='c-md-1'></div>

											</div>
										</div>

									</>
								)}
							</>
						)}

						<div className='form-section__last-block'>
							<div className='grid-row'>

								<div className='c-md-2 c-lg-3'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{siteKey ? <div className='recaptcha'>
										<ReCaptcha 
											siteKey={siteKey} 
											callback={(token) => recaptchaCallbackFunction(token, setVerified, validationRecaptchaUrl)}
											setVerified={setVerified}
										/>
									</div> : <></>}


									{privacyCheckbox && (
										<Checkbox
											name={privacyCheckbox?.name}
											required={privacyCheckbox?.required}
											errorMessages={privacyCheckbox?.errorMessages}
										>
											{privacyCheckbox?.text}
										</Checkbox>
									)}

									<Submit
										disabled={siteKey && !verified}
										label={submit?.label}
										successMessage={submit?.successMessage}
									/>

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

					</Form>
				</div>
			</Section>

		</div>
	)
}